// features/workstations/property-dossier/address-form.js
// ═══════════════════════════════════════════════════════════
// Address + deal-params input for the Property Deal Dossier.
// Submits { address, deal_params } to the tab's handleSubmit.
// Mirrors the role of signal-read/upload-zone.js.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';

const STRATEGIES = [
  { id: 'brrrr', label: 'BRRRR', hint: 'Buy · Rehab · Rent · Refinance · Repeat' },
  { id: 'flip',  label: 'Fix & Flip', hint: 'Rehab and resell for one-time profit' },
  { id: 'hold',  label: 'Buy & Hold', hint: 'Rental for long-term cash flow + appreciation' },
  { id: 'str',   label: 'Short-Term Rental', hint: 'STR / Airbnb — material-participation tax path' },
];

const REHAB_SCOPES = [
  { id: 'cosmetic', label: 'Cosmetic', range: '$15–25/sf' },
  { id: 'standard', label: 'Standard', range: '$30–45/sf' },
  { id: 'major', label: 'Major', range: '$50–75/sf' },
  { id: 'full_gut', label: 'Full Gut', range: '$80–120/sf' },
];

const card = {
  background: 'var(--surface-2)', border: '1px solid var(--border-3)',
  borderRadius: 12, padding: 24, maxWidth: 640,
};
const labelStyle = { display: 'block', fontSize: 12, color: '#888', marginBottom: 6, letterSpacing: 0.2 };
const inputStyle = {
  width: '100%', background: 'var(--surface-3)', border: '1px solid var(--border-2)',
  borderRadius: 8, padding: '10px 12px', color: 'inherit', fontSize: 14,
};

export default function AddressForm({ onSubmit, busy }) {
  const [address, setAddress] = useState('');
  const [strategy, setStrategy] = useState('brrrr');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [rehabCost, setRehabCost] = useState('');
  const [rehabMode, setRehabMode] = useState('manual'); // 'manual' | 'scope'
  const [scope, setScope] = useState('standard');
  const [pricePerSqft, setPricePerSqft] = useState('');

  // Consume an address staged by Ground Game ("→ Dossier" on a lead row).
  // One-shot: read once on mount, then clear.
  useEffect(() => {
    try {
      const staged = window.localStorage.getItem('shift_dossier_prefill_address');
      if (staged) {
        setAddress(staged);
        window.localStorage.removeItem('shift_dossier_prefill_address');
      }
    } catch { /* ignore */ }
  }, []);

  const canSubmit = address.trim().length > 4 && Number(purchasePrice) > 0 && !busy;

  const submit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    const rehab = rehabMode === 'scope'
      ? { rehabScope: scope, ...(Number(pricePerSqft) > 0 ? { rehabPricePerSqft: Number(pricePerSqft) } : {}) }
      : { rehabCost: Number(rehabCost) || 0 };
    onSubmit({
      address: address.trim(),
      deal_params: { strategy, purchasePrice: Number(purchasePrice), ...rehab },
    });
  };

  return (
    <form style={card} onSubmit={submit}>
      <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>Analyze a property</div>
      <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>
        Enter an address and your deal — get comps, ARV, the deal math (70% rule, BRRRR refi, cash flow),
        and a tax-strategy read for your situation.
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Property address</label>
        <input
          style={inputStyle}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="123 Example St, Tampa, FL 33602"
          autoFocus
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Strategy</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {STRATEGIES.map((s) => {
            const active = strategy === s.id;
            return (
              <button
                type="button"
                key={s.id}
                onClick={() => setStrategy(s.id)}
                style={{
                  textAlign: 'left', padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                  background: active ? 'rgba(200,169,110,0.12)' : 'var(--surface-3)',
                  border: `1px solid ${active ? '#c8a96e' : 'var(--border-2)'}`,
                  color: 'inherit',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: active ? '#c8a96e' : 'inherit' }}>{s.label}</div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{s.hint}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Purchase price</label>
        <input
          style={inputStyle} type="number" inputMode="numeric"
          value={purchasePrice} onChange={(e) => setPurchasePrice(e.target.value)}
          placeholder="100000"
        />
      </div>

      <div style={{ marginBottom: 22 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <label style={{ ...labelStyle, marginBottom: 0 }}>Rehab</label>
          <div style={{ display: 'flex', gap: 4 }}>
            {[['manual', 'Enter $'], ['scope', 'Estimate by scope']].map(([id, lbl]) => (
              <button type="button" key={id} onClick={() => setRehabMode(id)}
                style={{
                  fontSize: 11, padding: '4px 10px', borderRadius: 6, cursor: 'pointer', color: 'inherit',
                  background: rehabMode === id ? 'rgba(184,149,106,0.15)' : 'var(--surface-3)',
                  border: `1px solid ${rehabMode === id ? '#b8956a' : 'var(--border-2)'}`,
                }}>{lbl}</button>
            ))}
          </div>
        </div>

        {rehabMode === 'manual' ? (
          <input
            style={inputStyle} type="number" inputMode="numeric"
            value={rehabCost} onChange={(e) => setRehabCost(e.target.value)}
            placeholder="40000"
          />
        ) : (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {REHAB_SCOPES.map((s) => {
                const active = scope === s.id;
                return (
                  <button type="button" key={s.id} onClick={() => setScope(s.id)}
                    style={{
                      textAlign: 'left', padding: '8px 12px', borderRadius: 8, cursor: 'pointer', color: 'inherit',
                      background: active ? 'rgba(184,149,106,0.12)' : 'var(--surface-3)',
                      border: `1px solid ${active ? '#b8956a' : 'var(--border-2)'}`,
                    }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: active ? '#b8956a' : 'inherit' }}>{s.label}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>{s.range}</div>
                  </button>
                );
              })}
            </div>
            <input
              style={{ ...inputStyle, marginTop: 8 }} type="number" inputMode="numeric"
              value={pricePerSqft} onChange={(e) => setPricePerSqft(e.target.value)}
              placeholder="$/sqft override (optional — defaults to scope midpoint)"
            />
            <div style={{ fontSize: 11, color: '#666', marginTop: 6 }}>
              We’ll estimate rehab from the property’s square footage × $/sqft once the data loads (+10% contingency).
            </div>
          </div>
        )}
      </div>

      <button
        type="submit"
        disabled={!canSubmit}
        style={{
          width: '100%', padding: '12px 16px', borderRadius: 8, border: 'none',
          fontSize: 14, fontWeight: 600, cursor: canSubmit ? 'pointer' : 'not-allowed',
          background: canSubmit ? '#c8a96e' : 'var(--surface-4)',
          color: canSubmit ? '#1a1a1a' : '#666',
        }}
      >
        {busy ? 'Analyzing…' : 'Analyze deal'}
      </button>
      <div style={{ fontSize: 11, color: '#666', marginTop: 12, textAlign: 'center' }}>
        Education only, not financial or tax advice. Estimates depend on data quality + your full situation.
      </div>
    </form>
  );
}
